import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { ShoppingCart, Package, Search, X, LayoutDashboard, User, ChevronUp, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { logoUrl } from "../lib/media";
import AccountMenu, { AvatarInisial } from "./AccountMenu";
import ProfileDialog from "./ProfileDialog";
import AddressBookDialog from "./AddressBookDialog";
import "../styles/home-v5.css";

function SearchForm({ q, setQ, onSubmit, tid }) {
  return (
    <form onSubmit={onSubmit} className="relative w-full" role="search">
      <label htmlFor={tid} className="sr-only">Cari produk</label>
      <Search size={16} strokeWidth={1.6} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6E73] pointer-events-none" />
      <input
        id={tid}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Cari produk…"
        data-testid={tid}
        className="w-full h-10 rounded-[2px] border border-[#E5E5EA] bg-white pl-9 pr-3 text-sm text-[#1D1D1F] placeholder:text-[#6E6E73] outline-none focus:border-[#1D1D1F]"
      />
    </form>
  );
}

export default function CustomerNav() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { settings } = useSettings();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const diKatalog = loc.pathname === "/katalog" || loc.pathname.startsWith("/katalog/");
  const urlQ = params.get("q") || "";
  const [q, setQ] = useState(urlQ);
  const [cariHp, setCariHp] = useState(false);
  const [panel, setPanel] = useState(null); // 'desktop' | 'mobile' | null
  const [jendela, setJendela] = useState(null); // 'profil' | 'alamat' | null
  const [counts, setCounts] = useState({ belum: 0, diproses: 0, dikirim: 0 });

  const namaToko = settings?.nama_toko || "HoonStore";
  const logo = logoUrl(settings?.logo_url);

  useEffect(() => {
    if (!user || user.role === "admin") return;
    api.get("/api/orders").then(({ data }) => {
      const pesanan = data || [];
      const hitung = (list) => pesanan.filter((o) => list.includes(o.status)).length;
      setCounts({
        belum: hitung(["MENUNGGU_BAYAR"]),
        diproses: hitung(["MENUNGGU_KONFIRMASI", "DIPROSES", "DIBAYAR", "DIKEMAS"]),
        dikirim: hitung(["DIKIRIM"]),
      });
    }).catch(() => {});
  }, [user]);

  // sinkron isi kotak cari dengan ?q= di URL saat berada di Katalog
  useEffect(() => {
    if (diKatalog) setQ(params.get("q") || "");
  }, [loc.pathname, loc.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // di Katalog: saring langsung saat mengetik (jeda 300ms), URL pakai replace
  useEffect(() => {
    if (!diKatalog) return;
    const t = setTimeout(() => {
      const bersih = q.trim();
      if (bersih === urlQ) return;
      const sisa = new URLSearchParams(loc.search);
      if (bersih) sisa.set("q", bersih); else sisa.delete("q");
      const s = sisa.toString();
      nav(`${loc.pathname}${s ? `?${s}` : ""}`, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [q, diKatalog, urlQ]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (e) => {
    e.preventDefault();
    setCariHp(false);
    const bersih = q.trim();
    if (diKatalog) {
      const sisa = new URLSearchParams(loc.search);
      if (bersih) sisa.set("q", bersih); else sisa.delete("q");
      const s = sisa.toString();
      nav(`${loc.pathname}${s ? `?${s}` : ""}`, { replace: true });
      return;
    }
    nav(bersih ? `/katalog?q=${encodeURIComponent(bersih)}` : "/katalog");
  };

  const Badge = ({ tid = "nav-cart-count" }) => (count > 0 ? (
    <span data-testid={tid}
      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF6B1A] text-white text-[11px] font-semibold flex items-center justify-center">
      {count}
    </span>
  ) : null);

  const linkCls = "inline-flex items-center gap-2 h-10 px-3 text-sm font-medium text-[#424245] hover:text-[#1D1D1F] transition-colors";

  const keluar = () => { setPanel(null); logout(); nav("/login"); };

  const brand = (
    <Link to="/" data-testid="nav-brand-logo" className="shrink-0 self-stretch inline-flex items-center">
      <img src={logo} alt={namaToko} width="600" height="80" className="h-7 md:h-9 w-auto max-w-[140px] md:max-w-[200px] object-contain" />
    </Link>
  );

  const labelAkun = user?.nama_usaha || user?.nama || "Akun Saya";

  return (
    <>
      <header className="hsv5 sticky top-0 z-40 bg-white border-b border-[#E5E5EA]">
        {/* Desktop */}
        <div className="hidden md:flex mx-auto w-full max-w-[1440px] h-16 px-20 items-center justify-between gap-8">
          {brand}
          <div className="flex-1 max-w-[448px]">
            <SearchForm q={q} setQ={setQ} onSubmit={submit} tid="nav-search" />
          </div>
          <nav className="flex items-center gap-1 shrink-0">
            <Link to="/katalog" data-testid="nav-link-katalog" className={linkCls}><Package size={16} strokeWidth={1.6} /> Katalog</Link>
            {user?.role !== "admin" && (
              <Link to="/keranjang" data-testid="nav-link-keranjang" className={`${linkCls} relative`}>
                <span className="relative inline-flex"><ShoppingCart size={16} strokeWidth={1.6} /><Badge /></span>
                <span className="ml-1">Keranjang</span>
              </Link>
            )}
            {user?.role === "admin" && (
              <Link to="/admin" data-testid="nav-admin-switch" className={linkCls}><LayoutDashboard size={16} strokeWidth={1.6} /> Panel</Link>
            )}
            {user ? (
              <div className="relative">
                <button type="button" data-akun-trigger data-testid="nav-akun-btn"
                  onClick={() => setPanel((p) => (p === "desktop" ? null : "desktop"))}
                  className={`inline-flex h-10 max-w-[220px] items-center gap-2 rounded-[4px] px-3 text-sm font-medium text-[#424245] transition-colors hover:text-[#1D1D1F] ${panel === "desktop" ? "border border-[#E5E5EA]" : ""}`}>
                  <User size={16} strokeWidth={1.6} />
                  <span className="truncate">{labelAkun}</span>
                  {panel === "desktop" ? <ChevronUp size={15} strokeWidth={1.8} /> : <ChevronDown size={15} strokeWidth={1.8} />}
                </button>
                {panel === "desktop" && (
                  <AccountMenu user={user} counts={counts} variant="desktop" onClose={() => setPanel(null)}
                    onOpenProfile={() => setJendela("profil")} onOpenAddress={() => setJendela("alamat")} onLogout={keluar} />
                )}
              </div>
            ) : (
              <Link to="/login" data-testid="nav-login-btn"
                className="inline-flex items-center h-10 px-4 rounded-[2px] bg-[#1D1D1F] text-white text-sm font-semibold hover:bg-black transition-colors">
                Masuk
              </Link>
            )}
          </nav>
        </div>

        {/* Mobile (Panduan, S&K, halaman tidak ditemukan di HP): bar 48 px seperti kepala HP lain (INSTRUKSI PR46 butir 2); tombol tetap 44 px */}
        <div className="md:hidden">
          <div className="h-12 pl-4 pr-2 max-[359px]:pl-1 max-[359px]:pr-0 flex items-center justify-between">
            {brand}
            <div className="flex items-center">
              <Link to="/katalog" aria-label="Katalog" data-testid="nav-link-katalog-m"
                aria-current={diKatalog ? "page" : undefined}
                className={`w-11 h-11 inline-flex items-center justify-center ${diKatalog ? "text-[#FF6B1A]" : "text-[#424245]"}`}>
                <Package size={20} strokeWidth={1.6} />
              </Link>
              <button onClick={() => setCariHp((v) => !v)} aria-label="Cari produk" data-testid="nav-search-btn-m"
                className="w-11 h-11 inline-flex items-center justify-center text-[#424245]">
                {cariHp ? <X size={20} strokeWidth={1.6} /> : <Search size={20} strokeWidth={1.6} />}
              </button>
              {user?.role !== "admin" && (
                <Link to="/keranjang" aria-label="Keranjang" data-testid="nav-link-keranjang-m"
                  className="relative w-11 h-11 inline-flex items-center justify-center text-[#424245]">
                  <ShoppingCart size={20} strokeWidth={1.6} /><Badge tid="nav-cart-count-m" />
                </Link>
              )}
              {user?.role === "admin" && (
                <Link to="/admin" aria-label="Panel admin" data-testid="nav-admin-switch-m"
                  className="w-11 h-11 inline-flex items-center justify-center text-[#424245]">
                  <LayoutDashboard size={20} strokeWidth={1.6} />
                </Link>
              )}
              {user ? (
                <button type="button" data-akun-trigger data-testid="nav-akun-btn-m" aria-label="Menu akun"
                  onClick={() => setPanel((p) => (p === "mobile" ? null : "mobile"))}
                  className="ml-1 max-[359px]:ml-0 inline-flex h-11 w-11 items-center justify-center">
                  <AvatarInisial nama={user.nama} size={34} />
                </button>
              ) : (
                <Link to="/login" data-testid="nav-login-btn-m" className="ml-1 hidden min-[380px]:inline-flex h-11 items-center">
                  {/* PR46: bidang sentuh 44 px, kotak terlihat tetap 36 px */}
                  <span className="inline-flex h-9 items-center rounded-[2px] bg-[#1D1D1F] px-3 text-sm font-semibold text-white">Masuk</span>
                </Link>
              )}
              {!user && (
                <Link to="/login" aria-label="Masuk" data-testid="nav-login-icon-m"
                  className="ml-1 max-[359px]:ml-0 min-[380px]:hidden inline-flex h-11 w-11 items-center justify-center text-[#424245]">
                  <User size={20} strokeWidth={1.6} />
                </Link>
              )}
            </div>
          </div>
          {cariHp && (
            <div className="px-4 pb-3">
              <SearchForm q={q} setQ={setQ} onSubmit={submit} tid="nav-search-m" />
            </div>
          )}
        </div>
      </header>

      {panel === "mobile" && user && (
        <AccountMenu user={user} counts={counts} variant="mobile" onClose={() => setPanel(null)}
          onOpenProfile={() => setJendela("profil")} onOpenAddress={() => setJendela("alamat")} onLogout={keluar} />
      )}
      {jendela === "profil" && <ProfileDialog onClose={() => setJendela(null)} />}
      {jendela === "alamat" && <AddressBookDialog onClose={() => setJendela(null)} />}
    </>
  );
}
